import { ContentType } from '@entries/content-types'
import ContentfulNumberHighlight from '@entries/contentful-number-highlight'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import clsx from 'clsx/lite'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'

const ContentfulMultipleNumberHighlight: Utils.EntryFC<
  Queries.ContentfulNumbersHighlight
> = ({ content: { numbers, backgroundImage } }) => {
  const {
    ContrasteLineaire,
    LabyrintheTurquoise,
    Marbre,
    RayuresVertes,
    TerrazzoOrange,
    TerrazzoVert,
  } = useStaticQuery<Queries.ContentfulMultipleNumberHighlightAssetsQuery>(
    graphql`
      query ContentfulMultipleNumberHighlightAssets {
        ContrasteLineaire: contentfulAsset(
          contentful_id: { eq: "5nSMWW42EtVmXwIeARNLRy" }
        ) {
          ...CardComparatorAsset
        }
        LabyrintheTurquoise: contentfulAsset(
          contentful_id: { eq: "17tSdEejKaZVfxfTBIA4Og" }
        ) {
          ...CardComparatorAsset
        }
        Marbre: contentfulAsset(
          contentful_id: { eq: "ZNnn2qHXuYDnogsX9UD2v" }
        ) {
          ...CardComparatorAsset
        }
        RayuresVertes: contentfulAsset(
          contentful_id: { eq: "1mJVxfsbeI3hyCNQYi5wMA" }
        ) {
          ...CardComparatorAsset
        }
        TerrazzoOrange: contentfulAsset(
          contentful_id: { eq: "1ynDcDVqmQkW8nj51ZDEBF" }
        ) {
          ...CardComparatorAsset
        }
        TerrazzoVert: contentfulAsset(
          contentful_id: { eq: "6WJF7eM2l05YPGl7dn4vaz" }
        ) {
          ...CardComparatorAsset
        }
      }
    `
  )

  const ImageNameContentfulIdMapper = {
    'Contraste Linéaire': ContrasteLineaire,
    'Labyrinthe Turquoise': LabyrintheTurquoise,
    Marbre: Marbre,
    'Rayures Vertes': RayuresVertes,
    'Terrazzo Orange': TerrazzoOrange,
    'Terrazzo Vert': TerrazzoVert,
  }
  type BackgroundImageKey = keyof typeof ImageNameContentfulIdMapper

  return (
    <div className="grid h-fit w-full place-self-center overflow-hidden rounded rounded-[24px] mobileL:max-w-[576px] tablet:max-w-none">
      <GatsbyImage
        className="flex h-full w-full overflow-hidden rounded-[0.625rem]"
        alt={
          ImageNameContentfulIdMapper[backgroundImage as BackgroundImageKey]
            .description
        }
        image={getImage(
          ImageNameContentfulIdMapper[backgroundImage as BackgroundImageKey]
        )}
        style={{
          gridArea: '1/1',
        }}
      />
      <div
        className={clsx(
          'relative grid grid-cols-1 place-items-center content-stretch gap-5 p-6 tablet:aspect-square tablet:gap-5 tablet:p-7 laptop:p-12',
          `tablet:grid-cols-${numbers.length === 4 ? 2 : 1}`
        )}
        style={{
          gridArea: '1/1',
        }}
      >
        {numbers.map((number, index) => (
          <Entry
            key={index}
            content={number}
            mapping={{
              [ContentType.ContentfulNumberHighlight]: (props) => (
                <ContentfulNumberHighlight
                  titleSize={numbers.length === 1 ? 'big' : 'small'}
                  {...props}
                />
              ),
            }}
          />
        ))}
      </div>
    </div>
  )
}

export const query = graphql`
  fragment ContentfulNumbersHighlight on ContentfulNumbersHighlight {
    ...ContentfulEntry
    backgroundImage
    numbers {
      ...ContentfulNumberHighlight
    }
  }
`

export default withDisplayName(
  ContentfulMultipleNumberHighlight,
  'ContentfulMultipleNumberHighlight'
)
